import { Button, Form } from '@/components';
import { sessionAPI } from '@/libs/api';
import { useCountdown, useForm, useMounted } from '@/libs/hooks';
import { appStore, useAppStore } from '@/store';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { ButtonSubmit } from '../UserSignin/styles';
import { Message } from './Message';
import { Container, TimeWrapper } from './styles';
import { initialErrors, initialValues, validateForm } from './validation';

const ValidateOTP: FC = () => {
  const OTP_DURATION = 180;
  const { count, startCount } = useCountdown(OTP_DURATION);
  const [OTPCount, setOTPCount] = useState(1);
  const [isError, setError] = useState(false);

  const isMounted = useMounted();

  const {
    token,
    userSession: {
      user: { name, user_ref },
    },
  } = useAppStore();

  const handleResendOTP = useCallback(async () => {
    const newOTPCount = OTPCount + 1;
    setOTPCount(newOTPCount);
    startCount(OTP_DURATION);

    try {
      await sessionAPI.claimSession(
        {
          user_ref,
          name,
          otp_resend: OTPCount,
        },
        token || '',
      );
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }, [OTPCount, name, startCount, token, user_ref]);

  const handleValidateOTP = useCallback(
    async (data: typeof initialValues) => {
      if (data?.OTPCode?.length === 4 && name && user_ref) {
        try {
          const { success, data: resData } = await sessionAPI.claimSession(
            {
              user_ref,
              name,
              otp_verify: data.OTPCode,
            },
            token || '',
          );
          if (success) {
            appStore.setUserSession(resData);
            appStore.setActiveScreen('CONVERSATION');
          }
        } catch (error) {
          if (isMounted()) {
            setError(true);
          }
        }
      }
    },
    [isMounted, name, token, user_ref],
  );

  const { values, errors, setErrors, handleSubmit, handleOTPChange } = useForm({
    initialValues,
    initialErrors,
    validate: validateForm,
    onSuccess: handleValidateOTP,
  });

  useEffect(() => {
    if (isError) {
      setErrors({ OTPCode: 'Your OTP is not correct' });
      setError(false);
    } else if (count === 0) {
      setErrors({ OTPCode: 'Your OTP is expired' });
      setError(false);
    }
  }, [count, isError, setErrors]);

  return (
    <Container>
      <Message />
      <form onSubmit={handleSubmit}>
        <Form.OTP
          name="OTPCode"
          value={values.OTPCode}
          onOTPChange={(data) => handleOTPChange(data)}
          variant={errors.OTPCode ? 'danger' : undefined}
          message={errors.OTPCode}
          style={{ marginBottom: '2rem' }}
        />
        <TimeWrapper>
          <span>{count > 0 ? `Time Remaining ${count}s` : "Don't have the OTP?"}</span>
          <Button type="button" variant="link" onClick={handleResendOTP} disabled={count > 0}>
            Resend
          </Button>
        </TimeWrapper>
        <ButtonSubmit type="submit" size="lg" block>
          Verify OTP & Proceed
        </ButtonSubmit>
      </form>
    </Container>
  );
};

export default memo(ValidateOTP);
